import { NavLink, Outlet, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, ClipboardList, Home as HomeIcon, DollarSign, TrendingUp,
  Banknote, LineChart, Truck, Boxes, Store, LogOut, Menu, X, ArrowLeft, KeyRound, Send, ArrowLeftRight, Syringe, Trash2,
  Clock, CalendarDays, Plane, Users, Wallet, Receipt, ListChecks, ShieldCheck, StickyNote, CheckSquare,
} from "lucide-react";
import api, { LOGO_URL, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PushToggle } from "@/components/PushToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function ChangePasswordButton() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [loading, setLoading] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (nw.length < 6) return toast.error("New password must be at least 6 characters.");
    if (nw !== cf) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated.");
      setOpen(false); setCur(""); setNw(""); setCf("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password.");
    } finally { setLoading(false); }
  }
  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)} data-testid="admin-change-password-open"
        className="w-full justify-start text-teal-100 hover:bg-teal-900/60 hover:text-white">
        <KeyRound className="mr-2 h-4 w-4" /> Change Password
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2"><Label>Current password</Label><Input type="password" data-testid="cp-current" value={cur} onChange={(e) => setCur(e.target.value)} required /></div>
            <div className="space-y-2"><Label>New password</Label><Input type="password" data-testid="cp-new" value={nw} onChange={(e) => setNw(e.target.value)} required /></div>
            <div className="space-y-2"><Label>Confirm new password</Label><Input type="password" data-testid="cp-confirm" value={cf} onChange={(e) => setCf(e.target.value)} required /></div>
            <Button type="submit" disabled={loading} data-testid="cp-submit" className="w-full rounded-full">{loading ? "Saving..." : "Update Password"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ResetDemoDataButton() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  async function submit() {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/reset-data");
      toast.success(`Demo data cleared — ${data.total} record(s) removed. Logins kept.`);
      setOpen(false); setConfirmText("");
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reset data.");
    } finally { setLoading(false); }
  }
  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)} data-testid="admin-reset-data-open"
        className="w-full justify-start text-red-300 hover:bg-red-950/50 hover:text-red-200">
        <Trash2 className="mr-2 h-4 w-4" /> Reset Demo Data
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reset Demo Data</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>This permanently deletes <strong className="text-foreground">all operational data</strong>: orders, deliveries, transfers, rent, income, cash advances, inventory, OTC products &amp; reservations, immunizations, shipping &amp; telemed appointments, GLP/refill reminders, insurances and loyalty.</p>
            <p className="rounded-lg bg-secondary/50 p-3 text-foreground">Your staff <strong>logins</strong> and the <strong>admin gate password</strong> are kept. This cannot be undone.</p>
            <div className="space-y-2">
              <Label htmlFor="reset-confirm">Type <span className="font-mono font-bold text-foreground">RESET</span> to confirm</Label>
              <Input id="reset-confirm" data-testid="admin-reset-confirm-input" value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)} placeholder="RESET" autoComplete="off" />
            </div>
          </div>
          <Button variant="destructive" disabled={confirmText !== "RESET" || loading} onClick={submit}
            data-testid="admin-reset-confirm" className="w-full rounded-full">
            {loading ? "Clearing..." : "Delete All Demo Data"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}


const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Order History", icon: ClipboardList },
  { to: "/admin/order-log", label: "Order Log", icon: ListChecks },
  { to: "/admin/deliveries", label: "Deliveries", icon: Truck },
  { to: "/admin/transfers", label: "Transfers", icon: ArrowLeftRight },
  { to: "/admin/glp-log", label: "GLP Reorder Log", icon: Syringe },
  { to: "/admin/vaccine-drives", label: "Vaccine Drives", icon: Syringe },
  { to: "/admin/shipping", label: "Shipping", icon: Send },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/otc", label: "OTC Store Editor", icon: Store },
  { to: "/admin/rent", label: "Rent", icon: HomeIcon },
  { to: "/admin/cash-advance", label: "Cash Advances", icon: Banknote },
  { to: "/admin/income", label: "Income", icon: TrendingUp },
  { to: "/admin/costs", label: "Costs", icon: DollarSign },
  { to: "/admin/profit-loss", label: "Profit & Loss", icon: LineChart },
  { to: "/admin/payroll", label: "Payroll", icon: Wallet },
  { to: "/admin/pay-day-calendar", label: "Pay Day Calendar", icon: CalendarDays },
];

const WORKFORCE_NAV = [
  { to: "/admin/clock", label: "Time Clock", icon: Clock },
  { to: "/admin/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/admin/time-off", label: "Time Off", icon: Plane },
  { to: "/admin/pay-preview", label: "Pay Preview", icon: Receipt },
  { to: "/admin/notes", label: "Notes", icon: StickyNote },
  { to: "/admin/tasks", label: "Tasks", icon: CheckSquare },
];

// Operational pages a worker may open (everything except the financial pages, Overview & Staff).
const WORKER_PATHS = ["/admin/orders", "/admin/order-log", "/admin/deliveries", "/admin/transfers", "/admin/glp-log", "/admin/shipping", "/admin/inventory", "/admin/otc"];
const ADMIN_ONLY_PATHS = ["/admin", "/admin/rent", "/admin/cash-advance", "/admin/income", "/admin/costs", "/admin/profit-loss", "/admin/payroll", "/admin/pay-day-calendar", "/admin/staff", "/admin/vaccine-drives"];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [myTasks, setMyTasks] = useState(0);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    let active = true;
    api.get("/staff/tasks").then(({ data }) => {
      if (active) setMyTasks(data.filter((t) => t.assignee_id === user?.id && t.status !== "done").length);
    }).catch(() => {});
    return () => { active = false; };
  }, [user?.id, location.pathname]);
  const nav = isAdmin
    ? [...NAV, ...WORKFORCE_NAV, { to: "/admin/staff", label: "Staff", icon: Users }]
    : [...NAV.filter((n) => WORKER_PATHS.includes(n.to)), ...WORKFORCE_NAV];

  if (!isAdmin && ADMIN_ONLY_PATHS.includes(location.pathname)) {
    return <Navigate to="/admin/clock" replace />;
  }

  const Sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-teal-900 px-5 py-5">
        <div className="rounded-lg bg-white p-1.5"><img src={LOGO_URL} alt="Crystal" className="h-7 w-auto" /></div>
        <span className="font-heading text-sm font-bold text-white">{isAdmin ? "Admin Portal" : "Staff Portal"}</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-white" : "text-teal-100 hover:bg-teal-900/60 hover:text-white"
              }`
            }
          >
            <n.icon className="h-4 w-4" /> {n.label}
            {n.to === "/admin/tasks" && myTasks > 0 && (
              <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-white" data-testid="nav-mytasks-badge">{myTasks}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-teal-900 p-3 space-y-1">
        <p className="px-2 pb-1 text-xs text-teal-300">{user?.email}</p>
        <Button asChild variant="ghost" data-testid="admin-back-website"
          className="w-full justify-start text-teal-100 hover:bg-teal-900/60 hover:text-white">
          <Link to="/"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Website</Link>
        </Button>
        <Button asChild variant="ghost" data-testid="admin-health-partner-portal"
          className="w-full justify-start text-teal-100 hover:bg-teal-900/60 hover:text-white">
          <Link to="/health-partners" target="_blank"><ShieldCheck className="mr-2 h-4 w-4" /> Health Partner Portal</Link>
        </Button>
        <ChangePasswordButton />
        <PushToggle />
        {isAdmin && <ResetDemoDataButton />}
        <Button variant="ghost" onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout"
          className="w-full justify-start text-teal-100 hover:bg-teal-900/60 hover:text-white">
          <LogOut className="mr-2 h-4 w-4" /> Log Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 bg-accent lg:block">{Sidebar}</aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-accent">{Sidebar}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-border bg-white px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu className="h-6 w-6" /></button>
          <span className="font-heading font-bold">Admin</span>
          <div className="w-6" />
        </header>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
